import { apiRequest } from '../api/client';
import { useSessionStore } from '../auth/session-store';

export interface CustomerSiteBreakdown {
  customerId: string;
  customerName: string;
  siteId: string;
  siteName: string;
  quantity: number;
}

export interface ProductionLine {
  menuId: string;
  menuName: string;
  serviceType: string;
  dietaryTags: string[];
  totalQuantity: number;
  byCustomerSite: CustomerSiteBreakdown[];
}

export interface ProductionSnapshot {
  lines: ProductionLine[];
  metadata: {
    serviceDate: string;
    totalQuantity: number;
    closedAt: string | null;
    closedBy: string | null;
    /** Absent on sheets from before the points of sale. */
    operatingUnit?: { id: string; name: string };
  };
}

/** Same shape as `ProductionLine`, plus the value it replaced — matches `apps/api`'s `SnapshotDelta`. */
export interface SnapshotDelta {
  added: ProductionLine[];
  removed: ProductionLine[];
  modified: (ProductionLine & { previousQuantity: number })[];
}

/** The registered-addendum shape embedded in `GET /production/:date`'s own `addenda` array — no `id`, unlike the row `POST .../addendum` answers with. */
export interface ProductionAddendumSummary {
  appliedAt: string;
  appliedBy: string;
  notes: string | null;
  changes: SnapshotDelta;
}

/** Matches `apps/api`'s `ProductionView` (`GET`/`POST close` share this exact envelope). */
export interface ProductionView {
  status: 'PROVISIONAL' | 'CLOSED';
  snapshot: ProductionSnapshot;
  addenda: ProductionAddendumSummary[];
  pendingDelta: SnapshotDelta;
}

/** The row `POST /production/:date/addendum` answers with — `null` on a silent no-op (no real delta). */
export interface ProductionSheetAddendumRecord {
  id: string;
  productionSheetId: string;
  orderChangeRequestId: string;
  changes: SnapshotDelta;
  appliedAt: string;
  appliedBy: string;
  notes: string | null;
}

export type ConsolidatedSheetStatus = 'SIN_PLANILLA' | 'PROVISIONAL' | 'CERRADA';

/** Matches `apps/api`'s `ConsolidatedProductionView` (`GET /production/:date/consolidated`). */
export interface ConsolidatedProduction {
  date: string;
  totals: { totalQuantity: number };
  items: ProductionLine[];
  byUnit: {
    operatingUnitId: string;
    name: string;
    sheetStatus: ConsolidatedSheetStatus;
    totalQuantity: number;
  }[];
}

/** `?operatingUnitId=` is what production routes read to pick the unit; omitted when none is named. */
function unitQuery(operatingUnitId: string | undefined): string {
  return operatingUnitId === undefined
    ? ''
    : `?operatingUnitId=${encodeURIComponent(operatingUnitId)}`;
}

/** Matches `apps/api` `GET /production/:date`. */
export function getProduction(
  serviceDate: string,
  operatingUnitId?: string,
): Promise<ProductionView> {
  return apiRequest<ProductionView>(`/production/${serviceDate}${unitQuery(operatingUnitId)}`);
}

/** Matches `apps/api` `GET /production/:date/consolidated`: read-only, across the caller's units. */
export function getConsolidatedProduction(serviceDate: string): Promise<ConsolidatedProduction> {
  return apiRequest<ConsolidatedProduction>(`/production/${serviceDate}/consolidated`);
}

/** Matches `apps/api` `POST /production/:date/close`. */
export function closeProduction(
  serviceDate: string,
  operatingUnitId?: string,
): Promise<ProductionView> {
  return apiRequest<ProductionView>(
    `/production/${serviceDate}/close${unitQuery(operatingUnitId)}`,
    { method: 'POST' },
  );
}

/**
 * Matches `apps/api` `POST /production/:date/addendum`: `changes` is never
 * sent by the caller, the server always recomputes the delta
 * (`production-addenda/spec.md`'s "Nota de diseño (resuelta)").
 */
export function registerAddendum(
  serviceDate: string,
  operatingUnitId?: string,
): Promise<ProductionSheetAddendumRecord | null> {
  return apiRequest<ProductionSheetAddendumRecord | null>(
    `/production/${serviceDate}/addendum${unitQuery(operatingUnitId)}`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  );
}

const DEFAULT_EXPORT_FILENAME_PREFIX = 'produccion-';

/**
 * `GET /production/:date/export?format=csv` answers CSV, not JSON
 * (`ExportProductionUseCase`'s own format, mirroring `exportOrders`'
 * documented deviation in `orders/api.ts`) — cannot go through
 * `apiRequest`, which always parses the body as JSON. Reads the real
 * filename from `Content-Disposition` rather than hardcoding one, since
 * it is date-specific here (`produccion-2026-09-25.csv`).
 */
export async function exportProduction(
  serviceDate: string,
  operatingUnitId?: string,
): Promise<void> {
  const unitParam =
    operatingUnitId === undefined ? '' : `&operatingUnitId=${encodeURIComponent(operatingUnitId)}`;

  await downloadCsv(`/production/${serviceDate}/export?format=csv${unitParam}`, serviceDate);
}

/** `GET /production/:date/consolidated/export?format=csv` (`produccion-{fecha}-todos.csv`). */
export async function exportConsolidatedProduction(serviceDate: string): Promise<void> {
  await downloadCsv(`/production/${serviceDate}/consolidated/export?format=csv`, serviceDate);
}

async function downloadCsv(path: string, serviceDate: string): Promise<void> {
  const token = useSessionStore.getState().session?.accessToken;
  const response = await fetch(`/api/v1${path}`, {
    headers: token === undefined ? {} : { authorization: `Bearer ${token}` },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('No pudimos exportar la planilla.');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download =
    filenameFromContentDisposition(response.headers.get('content-disposition')) ??
    `${DEFAULT_EXPORT_FILENAME_PREFIX}${serviceDate}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (header === null) {
    return null;
  }

  const match = /filename="([^"]+)"/.exec(header);

  return match?.[1] ?? null;
}
